import styled from "@emotion/styled";
import type { ReactNode } from "react";

import {
  MDSCard,
  MDSCardVariant,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSTextWeight,
} from "@/mds";
import { decodeAgentDisplayText } from "@/pages/agent/utils/agent-display-text.utils";
import { formatStatusLabel } from "@/pages/agent/utils/agent-status.utils";

export const AgentObjectHeroCard = ({
  actions,
  ariaLabel,
  children,
  context,
  description,
  emptyDescription = "No description yet.",
  error,
  status,
  title,
}: {
  actions?: ReactNode;
  ariaLabel: string;
  children?: ReactNode;
  context?: ReactNode;
  description?: string | null;
  emptyDescription?: string;
  error?: string | null;
  status?: string | null;
  title: string;
}) => (
  <StyledCard
    aria-label={decodeAgentDisplayText({ value: ariaLabel })}
    variant={MDSCardVariant.Chrome}
  >
    {context || actions ? (
      <StyledContextRow>
        <StyledContext>{context}</StyledContext>
        {actions ? <StyledActions>{actions}</StyledActions> : null}
      </StyledContextRow>
    ) : null}

    <StyledTitleRow>
      <StyledTitle
        as="h1"
        color={MDSTextColor.Primary}
        variant={MDSTextVariant.HeaderL}
        weight={MDSTextWeight.SemiBold}
      >
        {decodeAgentDisplayText({ value: title })}
      </StyledTitle>
      {status ? <AgentStatusPill>{formatStatusLabel({ status })}</AgentStatusPill> : null}
    </StyledTitleRow>

    {error ? <StyledError role="alert">{error}</StyledError> : null}

    <StyledDescription
      color={description ? MDSTextColor.Secondary : MDSTextColor.Tertiary}
      variant={MDSTextVariant.ParagraphS}
    >
      {decodeAgentDisplayText({ value: description || emptyDescription })}
    </StyledDescription>

    {children ? <StyledFooter>{children}</StyledFooter> : null}
  </StyledCard>
);

export const AgentStatusPill = styled.span`
  background: ${({ theme }) => theme.colors.background.button.chip.default};
  border-radius: ${({ theme }) => theme.borderRadius.max};
  color: ${({ theme }) => theme.colors.text.secondary};
  flex: 0 0 auto;
  font-size: ${({ theme }) => theme.fontSizes[MDSTextVariant.TextXs]};
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[2]}`};
`;

const StyledCard = styled(MDSCard)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  min-width: 0;
`;

const StyledContextRow = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  justify-content: space-between;
  min-width: 0;
`;

const StyledContext = styled.div`
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
`;

const StyledActions = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[1]};
  justify-content: flex-end;
`;

const StyledTitleRow = styled.div`
  align-items: flex-start;
  display: flex;
  gap: ${({ theme }) => theme.spacing[4]};
  justify-content: space-between;
  min-width: 0;
`;

const StyledTitle = styled(MDSText)`
  margin: 0;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const StyledDescription = styled(MDSText)`
  max-width: 760px;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
`;

const StyledError = styled.div`
  color: ${({ theme }) => theme.colors.text.danger};
  font-size: ${({ theme }) => theme.fontSizes[MDSTextVariant.TextS]};
`;

const StyledFooter = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.border.surface};
  padding-top: ${({ theme }) => theme.spacing[4]};
`;
